/**
 * Mikhmel Cornerstone4D Enterprise Dataset Manager
 * Director's Cut: Integrated with Smart Network Queue, Memory Guardian & GPU Cleanup
 */

import { cornerstone } from '../core/init.js';
import { log } from '../utils/logger.js';
import { networkQueue } from './NetworkQueue.js';
import { memoryGuardian } from './MemoryGuardian.js';
import { rememberVolume } from '../core/volumeRegistry.js';

// Helper: AbortSignal с поддержкой any() (Chrome 116+) и fallback
const combineSignals = (controller, timeoutMs) => {
  const signals = [controller.signal];
  if (timeoutMs && typeof AbortSignal.timeout === 'function') {
    signals.push(AbortSignal.timeout(timeoutMs));
  }
  return typeof AbortSignal.any === 'function' 
    ? AbortSignal.any(signals) 
    : controller.signal;
};

// Helper: yield для неблокирующих фоновых задач
const yieldToMain = () => {
  if (typeof scheduler !== 'undefined' && 'yield' in scheduler) return scheduler.yield();
  return new Promise(resolve => setTimeout(resolve, 0));
};

export class MikhmelDatasetManager extends EventTarget {
  #datasets = new Map();
  #cineLoops = new Map();
  #abortControllers = new Map();

  // ==========================================
  // 1. 4D CARDIAC CINE (SMART LAZY & LRU PROTECTED)
  // ==========================================

  async load4DCine(imageIdsByTimepoint, options = {}) {
    const { fps = 25, id = `cine_${crypto.randomUUID()}` } = options;

    const timepoints = Array.isArray(imageIdsByTimepoint[0])
      ? imageIdsByTimepoint
      : this.#groupByTimepoint(imageIdsByTimepoint);

    const abortController = new AbortController();
    this.#abortControllers.set(id, abortController);

    const signal = combineSignals(abortController, 60000);
    const firstFrameIds = timepoints[0];

    // МГНОВЕННЫЙ СТАРТ: первый кадр с приоритетом 0
    await Promise.allSettled(
      firstFrameIds.map(imageId => networkQueue.enqueue(imageId, 0, signal))
    );

    // Предрассчитываем плоский массив один раз — защита от GC-фризов в RAF
    const allImageIds = timepoints.flat();

    const dataset = {
      id, type: '4d-cine', timepoints, allImageIds,
      numTimepoints: timepoints.length, numSlices: firstFrameIds.length,
      fps, currentTimepoint: 0, playing: false, fullyLoaded: false, signal
    };

    this.#datasets.set(id, dataset);
    log.info(`[4D] Loaded CINE First Frame. Starting background prefetch...`);

    this.#prefetchCineFrames(dataset).catch(e => {
      if (e.name !== 'AbortError' && e.name !== 'TimeoutError') log.error(`[4D] Prefetch failed`, e);
    });

    return dataset;
  }

  async #prefetchCineFrames(dataset) {
    const remainingIds = dataset.allImageIds.slice(dataset.numSlices);
    const totalImages = dataset.allImageIds.length;
    let loadedImages = dataset.numSlices;

    const promises = remainingIds.map(async (imageId) => {
      try {
        await networkQueue.enqueue(imageId, 10, dataset.signal);
        loadedImages++;
        this.dispatchEvent(new CustomEvent('progress', {
          detail: { id: dataset.id, percent: Math.round((loadedImages / totalImages) * 100) }
        }));
      } catch (e) {
        if (e.name !== 'AbortError') log.warn(`[4D] Dropped frame: ${imageId}`);
      }
    });

    await Promise.allSettled(promises);
    if (!dataset.signal.aborted) {
      dataset.fullyLoaded = true;
      this.dispatchEvent(new CustomEvent('loaded', { detail: { id: dataset.id } }));
    }
  }

  playCine(datasetId, viewport) {
    const ds = this.#datasets.get(datasetId);
    if (!ds || ds.type !== '4d-cine') return;

    this.stopCine(datasetId);
    ds.playing = true;

    memoryGuardian.lock(ds.allImageIds);
    let lastFrameTime = performance.now();

    const loop = (currentTime) => {
      if (!ds.playing) return;
      const deltaTime = currentTime - lastFrameTime;
      const frameInterval = 1000 / ds.fps;

      if (deltaTime >= frameInterval) {
        ds.currentTimepoint = (ds.currentTimepoint + 1) % ds.numTimepoints;
        const imageIds = ds.timepoints[ds.currentTimepoint];
        
        const isReady = imageIds.every(id => cornerstone.cache?.getImageLoadObject?.(id));
        
        if (viewport && isReady) {
          viewport.setStack(imageIds).then(() => viewport.render());
          // Touch с примерным размером кадра для точной эвристики MemoryGuardian
          imageIds.forEach(id => memoryGuardian.touch(id, 1024 * 1024 * 2));
        }
        lastFrameTime = currentTime - (deltaTime % frameInterval);
      }
      this.#cineLoops.set(datasetId, requestAnimationFrame(loop));
    };

    this.#cineLoops.set(datasetId, requestAnimationFrame(loop));
    this.dispatchEvent(new CustomEvent('cine-play', { detail: { id: datasetId, playing: true } }));
  }

  stopCine(datasetId) {
    const rAF_ID = this.#cineLoops.get(datasetId);
    if (rAF_ID) cancelAnimationFrame(rAF_ID);
    this.#cineLoops.delete(datasetId);
    
    const ds = this.#datasets.get(datasetId);
    if (ds) {
      ds.playing = false;
      memoryGuardian.unlock(ds.allImageIds);
      this.dispatchEvent(new CustomEvent('cine-play', { detail: { id: datasetId, playing: false } }));
    }
  }

  setCineTimepoint(datasetId, timepoint, viewport = null) {
    const ds = this.#datasets.get(datasetId);
    if (!ds) return null;
    
    ds.currentTimepoint = Math.max(0, Math.min(timepoint, ds.numTimepoints - 1));
    const currentImages = ds.timepoints[ds.currentTimepoint];

    if (viewport && currentImages?.length) {
      viewport.setStack(currentImages).then(() => viewport.render());
      currentImages.forEach(id => memoryGuardian.touch(id));
      this.dispatchEvent(new CustomEvent('cine-frame', { 
        detail: { id: datasetId, frame: ds.currentTimepoint } 
      }));
    }
    return currentImages;
  }

  setCineFPS(datasetId, fps, viewport = null) {
    const ds = this.#datasets.get(datasetId);
    if (!ds) return;
    ds.fps = Math.max(1, fps);
    if (ds.playing && viewport) this.playCine(datasetId, viewport);
  }

  // ==========================================
  // 2. PET-CT FUSION (SAFE INITIALIZATION + ROLLBACK)
  // ==========================================

  async loadPETCTFusion(ctImageIds, petImageIds, options = {}) {
    const {
      id = `fusion_${crypto.randomUUID()}`,
      autoRegister = true,
      petColormap = 'hot',
      petOpacity = 0.5
    } = options;

    const abortController = new AbortController();
    this.#abortControllers.set(id, abortController);

    this.dispatchEvent(new CustomEvent('progress', { detail: { id, percent: 10 } }));

    const ctVolumeId = `ct_${id}`;
    const petVolumeId = `pet_${id}`;

    let ctVolume, petVolume;
    try {
      [ctVolume, petVolume] = await Promise.all([
        cornerstone.volumeLoader.createAndCacheVolume(ctVolumeId, { imageIds: ctImageIds }),
        cornerstone.volumeLoader.createAndCacheVolume(petVolumeId, { imageIds: petImageIds })
      ]);
    } catch (e) {
      // ROLLBACK: если один том упал — подчищаем второй, чтобы не было утечки кэша
      try { cornerstone.cache?.removeVolumeLoadObject?.(ctVolumeId); } catch {}
      try { cornerstone.cache?.removeVolumeLoadObject?.(petVolumeId); } catch {}
      throw e;
    }

    if (abortController.signal.aborted) throw new DOMException('Aborted', 'AbortError');
    this.dispatchEvent(new CustomEvent('progress', { detail: { id, percent: 40 } }));

    await Promise.all([ctVolume.load(), petVolume.load()]);

    if (abortController.signal.aborted) throw new DOMException('Aborted', 'AbortError');
    this.dispatchEvent(new CustomEvent('progress', { detail: { id, percent: 90 } }));

    const dataset = {
      id, type: 'pet-ct-fusion', 
      ctVolumeId, petVolumeId,
      ctImageIds, petImageIds, // КРИТИЧНО: сохраняем для applyFusion и destroyDataset
      petColormap, petOpacity, autoRegister, registered: false, 
      signal: abortController.signal
    };

    if (autoRegister) {
      const registerTask = () => this.#computeSimpleRegistration(ctVolume, petVolume);
      // [Next-Gen] Выносим тяжелые вычисления в background task
      if (typeof scheduler !== 'undefined' && 'postTask' in scheduler) {
        try {
          dataset.registrationMatrix = await scheduler.postTask(registerTask, { priority: 'background' });
        } catch {
          dataset.registrationMatrix = registerTask();
        }
      } else {
        dataset.registrationMatrix = registerTask();
      }
      dataset.registered = true;
    }

    memoryGuardian.lock([...ctImageIds, ...petImageIds]);
    this.#datasets.set(id, dataset);

    this.dispatchEvent(new CustomEvent('progress', { detail: { id, percent: 100 } }));
    this.dispatchEvent(new CustomEvent('loaded', { detail: { id } }));
    return dataset;
  }

  async applyFusion(datasetId, renderingEngine, viewportIds) {
    const ds = this.#datasets.get(datasetId);
    if (!ds || ds.type !== 'pet-ct-fusion') return;

    const volumeInputs = [
      { volumeId: ds.ctVolumeId },
      { volumeId: ds.petVolumeId, blendMode: cornerstone.Enums?.BlendModes?.MIP || 'MIP', slabThickness: Infinity },
    ];

    try {
      // Исправлен ReferenceError: переменные берутся из dataset
      rememberVolume(ds.ctVolumeId, ds.ctImageIds, viewportIds, {}, 'main');
      rememberVolume(ds.petVolumeId, ds.petImageIds, viewportIds, { opacity: ds.petOpacity }, 'overlay');
    } catch (e) { 
      log.warn('[Registry] Failed to remember volumes', e); 
    }

    await cornerstone.setVolumesForViewports(renderingEngine, volumeInputs, viewportIds);

    viewportIds.forEach(vpId => {
      const vp = renderingEngine.getViewport(vpId);
      if (vp) {
        vp.setProperties({ colormap: { name: ds.petColormap, opacity: ds.petOpacity } }, ds.petVolumeId);
        vp.render();
      }
    });
  }

  updateFusionProperties(datasetId, renderingEngine, viewportIds, updates = {}) {
    const ds = this.#datasets.get(datasetId);
    if (!ds || ds.type !== 'pet-ct-fusion') return;

    if (updates.opacity !== undefined) ds.petOpacity = Math.max(0, Math.min(1, updates.opacity));
    if (updates.colormap !== undefined) ds.petColormap = updates.colormap;

    viewportIds.forEach(vpId => {
      const vp = renderingEngine.getViewport(vpId);
      if (vp) {
        vp.setProperties({ colormap: { name: ds.petColormap, opacity: ds.petOpacity } }, ds.petVolumeId);
        vp.render();
      }
    });
  }

  // ==========================================
  // 3. WHOLE-BODY MRI (MULTI-STATION)
  // ==========================================

  async loadWholeBody(stations, options = {}) {
    const { id = `wb_${crypto.randomUUID()}`, autoStitch = true } = options;
    const abortController = new AbortController();
    this.#abortControllers.set(id, abortController);

    const signal = combineSignals(abortController, 300000); // 5 мин на всё исследование

    let loadedImages = 0;
    const totalImages = stations.reduce((sum, st) => sum + st.imageIds.length, 0);

    const stationData = await Promise.all(stations.map(async (st, i) => {
      if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
      
      const volumeId = `wb_st_${i}_${id}`;
      const volume = await cornerstone.volumeLoader.createAndCacheVolume(volumeId, { imageIds: st.imageIds });
      
      volume.load().then(() => {
        if (signal.aborted) return;
        loadedImages += st.imageIds.length;
        this.dispatchEvent(new CustomEvent('progress', {
          detail: { id, percent: Math.round((loadedImages / totalImages) * 100) }
        }));
      });

      try { rememberVolume(volumeId, st.imageIds, [], {}, 'main'); } catch (e) { /* */ }

      return { 
        volumeId, 
        imageIds: st.imageIds, 
        label: st.label || `Station ${i + 1}`, 
        position: st.position || { x: 0, y: 0, z: i * 500 } 
      };
    }));

    const dataset = { 
      id, type: 'whole-body', 
      stations: stationData, 
      stitched: false, 
      signal 
    };

    if (autoStitch && !signal.aborted) {
      dataset.stitchedVolumeId = await this.#stitchStations(stationData, id);
      dataset.stitched = true;
    }

    if (!signal.aborted) {
      this.#datasets.set(id, dataset);
      this.dispatchEvent(new CustomEvent('loaded', { detail: { id } }));
    }
    return dataset;
  }

  navigateToStation(datasetId, stationIndex) {
    const ds = this.#datasets.get(datasetId);
    if (!ds || ds.type !== 'whole-body') return null;
    
    const station = ds.stations[stationIndex];
    if (station) {
      memoryGuardian.lock(station.imageIds);
      station.imageIds.forEach(id => memoryGuardian.touch(id));
    }
    return station || null;
  }

  // ==========================================
  // 4. MEMORY & DATASET MANAGEMENT
  // ==========================================

  getDataset(id) { return this.#datasets.get(id); }
  getAllDatasets() { return Array.from(this.#datasets.values()); }

  destroyDataset(id) {
    const ds = this.#datasets.get(id);
    if (!ds) return;

    this.stopCine(id);

    const controller = this.#abortControllers.get(id);
    if (controller) {
      controller.abort();
      this.#abortControllers.delete(id);
    }

    try {
      if (ds.type === '4d-cine') {
        memoryGuardian.unlock(ds.allImageIds);
        ds.allImageIds.forEach(imgId => {
          if (cornerstone.cache?.removeImageLoadObject) {
            try { cornerstone.cache.removeImageLoadObject(imgId, { force: true }); } catch {}
          } else {
            try { cornerstone.imageLoader?.uncacheImage?.(imgId); } catch {}
          }
        });
      } else if (ds.type === 'pet-ct-fusion') {
        memoryGuardian.unlock([...ds.ctImageIds, ...ds.petImageIds]);
        cornerstone.cache?.removeVolumeLoadObject?.(ds.ctVolumeId);
        cornerstone.cache?.removeVolumeLoadObject?.(ds.petVolumeId);
      } else if (ds.type === 'whole-body') {
        ds.stations.forEach(st => {
          memoryGuardian.unlock(st.imageIds);
          cornerstone.cache?.removeVolumeLoadObject?.(st.volumeId);
        });
      }
    } catch (e) {
      log.error(`[Memory] Cache clear failed for ${id}`, e);
    }

    const volumeIds = this.#extractVolumeIds(ds);
    this.#datasets.delete(id);
    // ВАЖНО: Диспатчим volumeIds, чтобы UI-слой уничтожил WebGL текстуры!
    this.dispatchEvent(new CustomEvent('destroyed', { detail: { id, volumeIds } }));
    log.info(`[Memory] Dataset ${id} completely annihilated.`);
  }

  clearAll() {
    for (const id of this.#datasets.keys()) this.destroyDataset(id);
  }

  // ==========================================
  // 5. PRIVATE UTILITIES
  // ==========================================

  #groupByTimepoint(imageIds) {
    const chunkSize = Math.max(1, Math.floor(imageIds.length / 25));
    return Array.from({ length: Math.ceil(imageIds.length / chunkSize) }, (v, i) =>
      imageIds.slice(i * chunkSize, i * chunkSize + chunkSize)
    );
  }

  #computeSimpleRegistration(ctVolume, petVolume) {
    const identity = new Float32Array([1,0,0,0, 0,1,0,0, 0,0,1,0, 0,0,0,1]);
    try {
      const [ctx, cty, ctz] = ctVolume.origin || [0,0,0];
      const [ptx, pty, ptz] = petVolume.origin || [0,0,0];
      return new Float32Array([1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, ctx - ptx, cty - pty, ctz - ptz, 1]);
    } catch (e) { return identity; }
  }

  async #stitchStations(stationData, id) { 
    // TODO: Implement real stitching logic
    return `wb_stitched_${id}`; 
  }

  #extractVolumeIds(ds) {
    if (ds.type === 'pet-ct-fusion') return [ds.ctVolumeId, ds.petVolumeId];
    if (ds.type === 'whole-body') return ds.stations.map(s => s.volumeId);
    if (ds.stitchedVolumeId) return [ds.stitchedVolumeId];
    return [];
  }
}

export const datasetManager = new MikhmelDatasetManager();

export const PET_COLORMAPS = Object.freeze([
  'hot', 'hsv', 'jet', 'cool', 'spring', 'summer', 'autumn', 'winter', 'bone', 'copper', 'pet', 'pet20step',
]);

// ── Enterprise API ──
export function getDatasetManagerState() {
  const ds = datasetManager.getAllDatasets();
  const byType = {};
  for (const d of ds) byType[d.type] = (byType[d.type] || 0) + 1;
  return { totalDatasets: ds.length, byType, datasetIds: ds.map(d => d.id) };
}

export function getDatasetInfo(id) {
  const ds = datasetManager.getDataset(id);
  if (!ds) return null;
  return {
    id: ds.id, type: ds.type,
    ...(ds.type === '4d-cine' ? { 
      numTimepoints: ds.numTimepoints, numSlices: ds.numSlices, 
      fps: ds.fps, playing: ds.playing, 
      currentTimepoint: ds.currentTimepoint, fullyLoaded: ds.fullyLoaded 
    } : {}),
    ...(ds.type === 'pet-ct-fusion' ? { 
      ctVolumeId: ds.ctVolumeId, petVolumeId: ds.petVolumeId, 
      petColormap: ds.petColormap, petOpacity: ds.petOpacity, 
      registered: ds.registered 
    } : {}),
    ...(ds.type === 'whole-body' ? { 
      stationCount: ds.stations?.length, stitched: ds.stitched,
      stitchedVolumeId: ds.stitchedVolumeId
    } : {}),
  };
}

export function listPetColormaps() { return [...PET_COLORMAPS]; }